/**
 * AppSelect - Drop-in replacement for native <select> elements.
 * Uses Shadcn/Radix Select which renders as regular HTML divs/buttons,
 * immune to browser dark-mode form control styling issues.
 * 
 * Usage:
 *   <AppSelect value={val} onChange={setVal} options={[{value:'a', label:'A'}]} />
 *   <AppSelect value={val} onChange={setVal} placeholder="Choose...">
 *     <AppSelectItem value="a">Option A</AppSelectItem>
 *   </AppSelect>
 */
import React from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import { cn } from "@/lib/utils";

// Radix doesn't support empty string as value - use sentinel
const EMPTY = "__empty__";
const toRadix = (v) => (v === "" || v === null || v === undefined) ? EMPTY : String(v);
const fromRadix = (v) => v === EMPTY ? "" : v;

export function AppSelect({
  value, onChange, options, placeholder, className, disabled, children,
  triggerClassName, triggerStyle, "data-testid": testId, size = "default"
}) {
  const sizeClass = size === "sm" ? "h-7 text-xs px-2" : size === "lg" ? "h-10 text-sm px-3" : "h-9 text-sm px-3";

  return (
    <Select value={toRadix(value)} onValueChange={(v) => onChange(fromRadix(v))} disabled={disabled}>
      <SelectTrigger
        className={cn(
          "bg-white border-slate-200 text-slate-900 rounded-lg",
          sizeClass,
          triggerClassName,
          className
        )}
        style={triggerStyle}
        data-testid={testId}
      >
        <SelectValue placeholder={placeholder || "Vyberte..."} />
      </SelectTrigger>
      <SelectContent className="bg-white border-slate-200 text-slate-900 max-h-60 z-[200]">
        {options ? options.map((opt) => (
          <SelectItem
            key={opt.value ?? "__null__"}
            value={toRadix(opt.value)}
            className="text-slate-900 focus:bg-slate-100"
          >
            {opt.label}
          </SelectItem>
        )) : children}
      </SelectContent>
    </Select>
  );
}

export function AppSelectItem({ value, children, className }) {
  return (
    <SelectItem value={toRadix(value)} className={cn("text-slate-900 focus:bg-slate-100", className)}>
      {children}
    </SelectItem>
  );
}

export default AppSelect;
